import React, { useState, useRef, useEffect } from 'react';
import { useLang } from '../App';

const LANGUAGES = [
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'sq', flag: '🇦🇱', label: 'SQ' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'de', flag: '🇩🇪', label: 'DE' },
  { code: 'nl', flag: '🇳🇱', label: 'NL' },
];

export default function Navbar({ page, setPage }) {
  const [menuOpen,        setMenuOpen]        = useState(false);
  const [desktopLangOpen, setDesktopLangOpen] = useState(false);
  const [mobileLangOpen,  setMobileLangOpen]  = useState(false);
  const { lang, setLang, t } = useLang();

  const desktopLangRef = useRef(null);
  const mobileLangRef  = useRef(null);

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    function handleClick(e) {
      if (desktopLangRef.current && !desktopLangRef.current.contains(e.target)) {
        setDesktopLangOpen(false);
      }
      if (mobileLangRef.current && !mobileLangRef.current.contains(e.target)) {
        setMobileLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const dropdownStyle = {
    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
    background: '#fff', border: '1px solid #E8E8E8',
    borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    overflow: 'hidden', zIndex: 1000, minWidth: 140,
  };

  const langOptionStyle = (selected) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    width: '100%', padding: '10px 16px', border: 'none',
    background: selected ? '#FFF5F6' : '#fff',
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
    fontWeight: selected ? 700 : 500,
    color: selected ? '#C41230' : '#333',
    textAlign: 'left', transition: 'background 0.15s',
  });

  const checkmark = (
    <svg style={{ marginLeft: 'auto' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#C41230" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
    </svg>
  );

  return (
    <nav>
      <div className="nav-inner">

        {/* ── LOGO ── */}
        <div onClick={() => setPage('home')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoSVG />
          <span style={{
            fontFamily: "'Bebas Neue', 'Arial Black', sans-serif",
            fontSize: 26, letterSpacing: '0.06em', lineHeight: 1,
            color: '#1A1A1A', fontWeight: 900, userSelect: 'none',
          }}>
            RENT<span style={{ color: '#C41230' }}>355</span>
          </span>
        </div>

        {/* ── DESKTOP NAV ── */}
        <div className="nav-links">
          <button className={`nav-link ${page === 'home'    ? 'active' : ''}`} onClick={() => setPage('home')}>{t('nav_home')}</button>
          <button className={`nav-link ${page === 'cars'    ? 'active' : ''}`} onClick={() => setPage('cars')}>{t('nav_fleet')}</button>
          <button className={`nav-link ${page === 'contact' ? 'active' : ''}`} onClick={() => setPage('contact')}>{t('nav_contact')}</button>

          {/* Desktop language switcher */}
          <div ref={desktopLangRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setDesktopLangOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: desktopLangOpen ? '#f0f0f0' : 'transparent',
                border: '1px solid #E0E0E0', borderRadius: 8,
                padding: '6px 10px', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                color: '#333', transition: 'all 0.2s',
              }}
            >
              <span style={{ fontSize: 18 }}>{currentLang.flag}</span>
              <span>{currentLang.label}</span>
              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                style={{ transform: desktopLangOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            {desktopLangOpen && (
              <div style={dropdownStyle}>
                {LANGUAGES.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setDesktopLangOpen(false); }}
                    style={langOptionStyle(lang === l.code)}
                    onMouseEnter={e => { if (lang !== l.code) e.currentTarget.style.background = '#F7F7F7'; }}
                    onMouseLeave={e => { if (lang !== l.code) e.currentTarget.style.background = '#fff'; }}
                  >
                    <span style={{ fontSize: 20 }}>{l.flag}</span>
                    <span>{l.label}</span>
                    {lang === l.code && checkmark}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="btn-primary"
            style={{ padding: '9px 22px', fontSize: '13px', letterSpacing: '0.05em' }}
            onClick={() => setPage('admin')}
          >
            {t('nav_admin')}
          </button>
        </div>

        {/* ── MOBILE RIGHT ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

          {/* Mobile language switcher */}
          <div ref={mobileLangRef} style={{ position: 'relative' }} className="nav-mobile-lang">
            <button
              onClick={() => setMobileLangOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'transparent', border: '1px solid #E0E0E0',
                borderRadius: 8, padding: '5px 8px', cursor: 'pointer',
                fontSize: 20, lineHeight: 1,
              }}
            >
              {currentLang.flag}
            </button>

            {mobileLangOpen && (
              <div style={dropdownStyle}>
                {LANGUAGES.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setMobileLangOpen(false); }}
                    style={langOptionStyle(lang === l.code)}
                  >
                    <span style={{ fontSize: 20 }}>{l.flag}</span>
                    <span>{l.label}</span>
                    {lang === l.code && checkmark}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Hamburger */}
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            {menuOpen ? (
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            ) : (
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      {menuOpen && (
        <div className="nav-mobile-menu">
          <button className="nav-mobile-item" onClick={() => { setPage('home');    setMenuOpen(false); }}>{t('nav_home')}</button>
          <button className="nav-mobile-item" onClick={() => { setPage('cars');    setMenuOpen(false); }}>{t('nav_fleet')}</button>
          <button className="nav-mobile-item" onClick={() => { setPage('contact'); setMenuOpen(false); }}>{t('nav_contact')}</button>
          <button className="nav-mobile-item nav-mobile-admin" onClick={() => { setPage('admin'); setMenuOpen(false); }}>{t('nav_admin')}</button>
        </div>
      )}
    </nav>
  );
}

function LogoSVG() {
  return (
    <svg width="40" height="40" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="47" stroke="#C41230" strokeWidth="4" fill="#0C0C0C"/>
      <rect x="17" y="21" width="66" height="18" rx="9" fill="#C41230"/>
      <rect x="17" y="44" width="66" height="15" rx="7.5" fill="#4A4A4A"/>
      <rect x="17" y="63" width="52" height="14" rx="7" fill="#3A3A3A"/>
    </svg>
  );
}